/**
 * Usage-meter maths — pure, no React, no DOM, no imports.
 *
 * `UsageBar` (storage, the monthly invoice quota) and `StorageUsageBar` draw
 * from these, so the segment widths, the over-cap rule and the storage
 * thresholds are pinned by `tenant/frontend/hooks/usage.test.ts` instead of
 * living untested inside a `.tsx` the Node runner cannot import. Kept free of
 * imports on purpose: Node's runner resolves only explicit `.ts` paths.
 */

/** How close to its cap a usage is — the caller decides the thresholds,
 *  because they differ by resource (storage warns at 75 %, invoices at 80 %). */
export type UsageLevel = 'ok' | 'warn' | 'urgent' | 'full';

/** Share of the track the red over-cap part always gets, taken from the used
 *  part: a workshop that is already full and wants to add more must SEE the
 *  part that does not fit, not just a ring around a full bar. */
export const MIN_OVER_PCT = 8;

const clampPct = (v: number) => Math.max(0, Math.min(100, v));

/**
 * True when something PENDING would push the usage past the cap. Without a
 * pending part this is always `false`, even for `pct > 100`: an existing
 * caller that passes an unclamped share (the invoice quota after a mid-month
 * downgrade) keeps the plain full bar it always had.
 */
export function usageOverCap(pct: number | null, pendingPct?: number | null): boolean {
    if (pct == null) return false;
    const pending = Math.max(0, pendingPct ?? 0);
    return pending > 0 && pct + pending > 100;
}

export interface UsageSegments {
    /** Width of the used part, 0–100. */
    used: number;
    /** Width of the hatched pending part, 0–100; `used + pending <= 100`. */
    pending: number;
    over: boolean;
}

/**
 * The two widths of the track. While it fits, the pending part follows the
 * used part at its real size. Over the cap it fills the rest of the track and
 * never less than `MIN_OVER_PCT` (shrinking the used part to make room), so a
 * bar that is already at 100 % still shows its red part.
 *
 * `overCap` lets a caller that knows the exact amounts decide «over» itself:
 * `StorageUsageBar` passes the BYTE decision (`storageProjection().over`), so
 * a total of exactly the cap — whose two float shares can add up to
 * 100.00000000000001 — never draws red under a label that says it fits, nor
 * the other way round. Without it the shares decide (`usageOverCap`). Either
 * way nothing is over without a pending part.
 */
export function usageSegments(pct: number | null, pendingPct?: number | null, overCap?: boolean): UsageSegments {
    if (pct == null) return { used: 0, pending: 0, over: false };
    const used = clampPct(pct);
    const pending = Math.max(0, pendingPct ?? 0);
    const over = pending > 0 && (overCap ?? usageOverCap(pct, pending));
    if (!over) return { used, pending: Math.min(pending, 100 - used), over: false };
    const shown = Math.max(100 - used, MIN_OVER_PCT);
    return { used: 100 - shown, pending: shown, over: true };
}

/** The storage thresholds (same as the tenant banner + warning email):
 *  < 75 % ok · 75–89 % warn · 90–99 % urgent · 100 % full (uploads → 507). */
export function storageLevel(pct: number): UsageLevel {
    return pct >= 100 ? 'full' : pct >= 90 ? 'urgent' : pct >= 75 ? 'warn' : 'ok';
}

export interface StorageProjection {
    unlimited: boolean;
    /** Used share, clamped to 100. */
    pct: number;
    /** Pending share, unclamped (the bar cuts it). */
    pendingPct: number;
    level: UsageLevel;
    /** Level of used + pending: the hatch already warns before it lands. */
    projectedLevel: UsageLevel;
    /** Bytes by which used + pending exceed the cap (≤ 0 when it fits). */
    excess: number;
    /** Over the cap BECAUSE of something pending (the label says by how much). */
    over: boolean;
}

/** `StorageUsageBar`'s numbers. `capBytes` null or ≤ 0 = unlimited. */
export function storageProjection(usedBytes: number, capBytes: number | null, pendingBytes?: number | null): StorageProjection {
    const used = Math.max(0, usedBytes || 0);
    const pending = Math.max(0, pendingBytes || 0);
    const unlimited = capBytes == null || capBytes <= 0;
    if (unlimited) {
        return { unlimited, pct: 0, pendingPct: 0, level: 'ok', projectedLevel: 'ok', excess: 0, over: false };
    }
    const cap = capBytes as number;
    const pct = Math.min(100, (used / cap) * 100);
    const excess = used + pending - cap;
    return {
        unlimited,
        pct,
        pendingPct: (pending / cap) * 100,
        level: storageLevel(pct),
        projectedLevel: storageLevel(((used + pending) / cap) * 100),
        excess,
        over: pending > 0 && excess > 0,
    };
}
